import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  commandQuery,
  commandsShellQuery,
  commandsTableQuery,
  createCommand,
  updateCommand,
  type CommandForm,
  type CommandRow,
} from '../../api/settingsEaseedeskSkus'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { LoadFailed } from '../../ui/LoadFailed'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Commands list.
 *
 * Element cu element fata de `version-3/easeedesk_settings/skus/
 * commands_list.php` + `edit_command.php`, si gest cu gest fata de fisierele
 * `*-1.js` de langa ele.
 *
 * TEXTUL COMENZII trebuie sa fie unic. In portal se cerea numai la creare
 * (`callback_check_uniq_command` era pus doar pe ramura `new`), deci o editare
 * putea face doua comenzi cu acelasi text - si atunci nu se mai stie care se
 * executa. Aici se cere de amandoua dati.
 */

const PAGE = 25

const EMPTY: CommandForm = {
  command: '',
  command_title: '',
  command_type: 0,
  description: '',
  message: '',
  report_description_header: '',
  report_description: '',
}

function CommandModal({
  command,
  open,
  onClose,
  onSaved,
}: {
  command?: CommandRow
  open: boolean
  onClose(): void
  onSaved(): void
}) {
  const editing = command !== undefined
  const existing = useQuery(commandQuery(open && editing ? command.ID : 0))
  const [form, setForm] = useState<CommandForm>(EMPTY)

  useEffect(() => {
    if (!open) return
    if (!editing) {
      setForm(EMPTY)
      return
    }
    if (existing.data) {
      const row = existing.data.command
      setForm({
        command: row.command ?? '',
        command_title: row.command_title ?? '',
        command_type: Number(row.command_type ?? 0),
        description: row.description ?? '',
        message: row.message ?? '',
        report_description_header: row.report_description_header ?? '',
        report_description: row.report_description ?? '',
      })
    }
  }, [open, editing, existing.data])

  const save = useMutation({
    mutationFn: () => (editing ? updateCommand(command.ID, form) : createCommand(form)),
    onSuccess: () => {
      toast(editing ? 'Command updated.' : 'Command created.', 'Commands list')
      onClose()
      onSaved()
    },
    onError: (e) =>
      toast(e instanceof ApiError && e.message ? e.message : 'Something went wrong.', 'Error', 'error'),
  })

  const set = (patch: Partial<CommandForm>) => setForm({ ...form, ...patch })
  const ready = form.command.trim() !== '' && form.command_title.trim() !== '' && form.command_type > 0

  return (
    <Modal
      id={editing ? 'editCommandModal' : 'newCommandModal'}
      labelledBy={editing ? 'editCommandModalLabel' : 'newCommandModalLabel'}
      open={open}
      onClose={onClose}
      title={editing ? 'Edit command' : 'New command'}
    >
      <form
        id={editing ? 'new-commands-list-form-edit' : 'new-commands-list-form'}
        method="post"
        onSubmit={(e) => {
          e.preventDefault()
          if (!ready) return
          save.mutate()
        }}
      >
        <div className="row">
          <div className="col-md-4">
            <div className="form-group">
              <label className="control-label">
                Command <span className="required">*</span>
              </label>
              <input
                type="text"
                className="form-control input-sm"
                name="command"
                maxLength={100}
                value={form.command}
                onChange={(e) => set({ command: e.target.value })}
                required
              />
            </div>
          </div>
          <div className="col-md-5">
            <div className="form-group">
              <label className="control-label">
                Command Title <span className="required">*</span>
              </label>
              <input
                type="text"
                className="form-control input-sm"
                name="command_title"
                maxLength={100}
                value={form.command_title}
                onChange={(e) => set({ command_title: e.target.value })}
                required
              />
            </div>
          </div>
          <div className="col-md-3">
            <div className="form-group">
              <label className="control-label">
                Command Type <span className="required">*</span>
              </label>
              <select
                className="form-control input-sm"
                name="command_type"
                value={form.command_type}
                onChange={(e) => set({ command_type: Number(e.target.value) })}
                required
              >
                <option value={0}> - choose - </option>
                <option value={1}>Standard</option>
                <option value={2}>Operator</option>
              </select>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label className="control-label">Description</label>
              <textarea
                className="form-control input-sm"
                name="description"
                rows={3}
                value={form.description}
                onChange={(e) => set({ description: e.target.value })}
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label className="control-label">Message</label>
              <textarea
                className="form-control input-sm"
                name="message"
                rows={3}
                value={form.message}
                onChange={(e) => set({ message: e.target.value })}
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label className="control-label">Report Description Header</label>
              <textarea
                className="form-control input-sm"
                name="report_description_header"
                rows={3}
                value={form.report_description_header}
                onChange={(e) => set({ report_description_header: e.target.value })}
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label className="control-label">Report Description</label>
              <textarea
                className="form-control input-sm"
                name="report_description"
                rows={3}
                value={form.report_description}
                onChange={(e) => set({ report_description: e.target.value })}
              />
            </div>
          </div>
        </div>

        <hr className="dotted" />
        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button type="submit" className="btn btn-success" disabled={!ready || save.isPending}>
            {editing ? 'Edit' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

export function SkuCommandsPage() {
  const queryClient = useQueryClient()
  const shell = useQuery(commandsShellQuery())
  const [search, setSearch] = useState('')
  const [start, setStart] = useState(0)
  const [creating, setCreating] = useState(false)
  const [edited, setEdited] = useState<CommandRow | null>(null)

  const table = useQuery(commandsTableQuery(search, start, PAGE))

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'skus', 'commands'] })
  }

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (shell.error) return <LoadFailed what="The commands screen" error={shell.error} onRetry={() => void shell.refetch()} />
  if (!shell.data) return <Spinner />

  const rows = table.data?.data ?? []
  const total = table.data?.recordsTotal ?? 0

  return (
    <div>
      <h4>
        <strong>Commands</strong> list
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage, create or edit <strong>commands list</strong>
      </label>
      <button type="button" className="btn btn-success pull-right" onClick={() => setCreating(true)}>
        <i className="fas fa-plus" aria-hidden="true"></i> Create command
      </button>
      <hr />

      <div className="form-group" style={{ maxWidth: 320 }}>
        <input
          type="search"
          className="form-control input-sm"
          placeholder="Search"
          value={search}
          onChange={(e) => {
            setStart(0)
            setSearch(e.target.value)
          }}
        />
      </div>

      {table.error ? (
        <LoadFailed what="The commands" error={table.error} onRetry={() => void table.refetch()} />
      ) : !table.data ? (
        <Spinner />
      ) : (
        <table className="table table-bordered dataTable" width="100%" id="commands-list">
          <thead>
            <tr>
              <th className="a-left">Command</th>
              <th className="a-left">Command title</th>
              <th className="a-left">Command type</th>
              <th className="a-left">Description</th>
              <th className="a-left">Message</th>
              <th width={120}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center' }}>
                  Nothing to see yet.
                </td>
              </tr>
            ) : (
              rows.map((command) => (
                <tr key={command.ID} id={`row_${command.ID}`}>
                  <td>{command.command}</td>
                  <td>{command.command_title}</td>
                  <td>{Number(command.command_type) === 2 ? 'Operator' : 'Standard'}</td>
                  <td>{(command.description ?? '').slice(0, 100)}</td>
                  <td>{command.message ?? ''}</td>
                  <td className="center">
                    <button type="button" className="btn btn-xs btn-default" onClick={() => setEdited(command)}>
                      <i className="fas fa-pencil-alt" aria-hidden="true"></i> Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}

      {total > PAGE && (
        <div className="aright">
          <button
            type="button"
            className="btn btn-xs btn-default"
            disabled={start === 0}
            onClick={() => setStart(Math.max(0, start - PAGE))}
          >
            Previous
          </button>{' '}
          <span>
            {start + 1}-{Math.min(start + PAGE, total)} of {total}
          </span>{' '}
          <button
            type="button"
            className="btn btn-xs btn-default"
            disabled={start + PAGE >= total}
            onClick={() => setStart(start + PAGE)}
          >
            Next
          </button>
        </div>
      )}

      <CommandModal open={creating} onClose={() => setCreating(false)} onSaved={reload} />
      <CommandModal
        command={edited ?? undefined}
        open={edited !== null}
        onClose={() => setEdited(null)}
        onSaved={reload}
      />
    </div>
  )
}
